/* ----------------------------------------------------------------------------
 * SuggestWritePanel — Aura P2 on-screen cards (owner GO 10-10-2026): the 3 keyword
 * article suggestions for one site. Each card = keyword, rank position, target page
 * or "New article", working title and planned sections — SHOWN, never read out.
 * "Write them" (tap) or "yes" (voice) writes BookedCentral drafts; the P1 cards then
 * offer Draft / Publish. Presentational only: the caller owns the server calls.
 * -------------------------------------------------------------------------- */

import { cn } from '@/lib/utils';
import { decisionLabel, type SuggestWriteView } from './suggestWriteModel.ts';
import { AI_LIMIT_TITLE, limitScreenText } from './aiWritingLimit.ts';

export interface SuggestWritePanelProps {
  view: SuggestWriteView;
  onWrite: () => void;
  /** tap "Write this one" on one card (single blog: "Write it"). */
  onWriteOne?: (index: number) => void;
  onChooseSite: (siteId: number) => void;
  onDismiss: () => void;
  /** 'voice' = the dark Aura popup; 'rail' = the light Copilot rail. */
  variant?: 'voice' | 'rail';
}

function resultLabel(status: string): string {
  switch (status) {
    case 'writing': return 'Writing…';
    case 'written': return 'Written (draft in BookedCentral)';
    case 'duplicate': return 'Skipped — already have this article';
    case 'not_synced': return 'Needs a site page sync first';
    case 'refused': return 'Not written — monthly AI limit';
    case 'skipped': return 'Not written';
    default: return 'Failed';
  }
}

export function SuggestWritePanel({ view, onWrite, onWriteOne, onChooseSite, onDismiss, variant = 'rail' }: SuggestWritePanelProps) {
  const dark = variant === 'voice';
  const t = {
    box: dark ? 'border-white/10 bg-white/[0.03] text-[#e8e9ee]' : 'border-border-default bg-canvas/40 text-text-primary',
    sub: dark ? 'text-[#9aa0ad]' : 'text-text-tertiary',
    card: dark ? 'border-white/10 bg-[#0f0e16]' : 'border-border-default bg-surface',
    btn: dark ? 'border-white/15 bg-white/5 text-[#e8e9ee] hover:bg-white/10' : 'border-border-default bg-surface text-text-primary hover:bg-canvas',
    primary: dark ? 'border-[#a78bfa]/50 bg-[#a78bfa]/20 text-white hover:bg-[#a78bfa]/30' : 'border-ai/60 bg-ai-wash/60 text-text-primary hover:border-ai hover:bg-ai-wash',
  };
  const busy = view.phase === 'writing' || view.phase === 'loading';
  const canWrite = view.phase === 'asking' && view.writeAvailable;
  const unwritten = view.items.filter((c) => view.results[c.index]?.status !== 'written' && view.results[c.index]?.status !== 'duplicate');
  const single = view.kind === 'single';

  return (
    <section className={cn('flex flex-col gap-2 rounded-xl border p-3 text-[12px]', t.box)} aria-label={single ? 'Blog to write' : 'Suggested articles for your top keywords'} aria-busy={busy}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium leading-snug">{view.line || 'Looking at your top keywords…'}</p>
        <button type="button" onClick={onDismiss} className={cn('rounded-md border px-2 py-0.5 text-[11px]', t.btn)} aria-label="Close">
          Close
        </button>
      </div>

      {view.phase === 'choose_site' && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Choose a site">
          {view.sites.map((s) => (
            <button key={s.id} type="button" onClick={() => onChooseSite(s.id)} className={cn('min-h-[40px] rounded-md border px-3 py-1.5', t.btn)}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {view.items.length > 0 && (
        <ol className="flex flex-col gap-2">
          {view.items.map((c) => {
            const r = view.results[c.index];
            return (
              <li key={c.index} className={cn('rounded-lg border p-2.5', t.card)}>
                <div className="font-semibold leading-snug">{c.working_title}</div>
                <dl className={cn('mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]', t.sub)}>
                  <dt>Keyword</dt><dd className="truncate">{c.keyword}</dd>
                  <dt>Rank</dt><dd>{c.position != null ? 'Position ' + c.position : 'Not ranked'}</dd>
                  <dt>Target</dt><dd className="truncate" title={c.target_page ?? undefined}>{decisionLabel(c.decision, c.target_page_title || c.target_page)}</dd>
                  {c.link_target && c.decision !== 'refresh' && (<><dt>Links to</dt><dd className="truncate" title={c.link_target}>{c.link_target}</dd></>)}
                  {r && (<><dt>Status</dt><dd>{resultLabel(r.status)}</dd></>)}
                </dl>
                {c.outline.length > 0 && (
                  <details className="mt-1">
                    <summary className={cn('cursor-pointer text-[11px]', t.sub)}>Planned sections</summary>
                    <ul className={cn('mt-1 list-disc pl-4 text-[11px]', t.sub)}>
                      {c.outline.map((o) => <li key={o}>{o}</li>)}
                    </ul>
                  </details>
                )}
                {c.note && <p className={cn('mt-1 text-[11px] leading-snug', t.sub)}>{c.note}</p>}
                {r && r.status !== 'writing' && r.status !== 'written' && r.line && <p className="mt-1 text-[11px]" role="status">{r.line}</p>}
                {canWrite && !single && onWriteOne && unwritten.length > 1 && !r && (
                  <button type="button" disabled={busy} onClick={() => onWriteOne(c.index)} className={cn('mt-1.5 min-h-[36px] rounded-md border px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50', t.btn)}>
                    Write this one
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {canWrite && (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={onWrite} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold disabled:opacity-50', t.primary)}>
            {single || unwritten.length === 1 ? 'Write it' : `Write them (${unwritten.length})`}
          </button>
          <button type="button" disabled={busy} onClick={onDismiss} className={cn('min-h-[40px] rounded-md border px-3 py-1.5 font-semibold disabled:opacity-50', t.btn)}>
            Not now
          </button>
        </div>
      )}
      {view.limit && (
        <div role="alert" className={cn('rounded-lg border p-2.5 text-[12px] leading-snug', dark ? 'border-amber-400/50 bg-amber-400/10 text-amber-100' : 'border-amber-500/60 bg-amber-50 text-amber-900')}>
          <p className="font-semibold">{AI_LIMIT_TITLE}{view.limit.cap ? ` — ${view.limit.spent || '?'} of ${view.limit.cap} used` : ''}</p>
          <p className="mt-0.5">{limitScreenText(view.limit)}</p>
        </div>
      )}
      {view.limitMessage && <p className={cn('text-[11px]', t.sub)} role="status">{view.limitMessage}</p>}
      {view.resultLine && <p className="text-[11px]" aria-live="polite">{view.resultLine}</p>}
      {view.error && <p className="text-[11px] text-red-400" role="alert">{view.error}</p>}
    </section>
  );
}
